import { reactive } from 'vue'
import { DevicePid } from '@/define/define'
import { DriverService } from './driver/driver'
import { CurrentDevice, ResDeviceState } from '@libs/types/response'
import { STORAGE_KEYS } from '@libs/define/storage_key'
import { IS_INTERNAL_BUILD } from '@libs/utils/envUtils'
export enum DeviceProtocol {
  none = 0,
  dll_exe = 1,
  hid_web = 2,
}
const DefaultDevice: CurrentDevice = {
  ID: DevicePid.None,
  IsSupportMagnet: false,
  ConnectMode: 0,
  ProductName: '',
  Limit: {
    DpiLen: 0,
    KeysColorLen: 0,
    MacroIndexLen: 0,
    MacroBytesLen: 0,
    MacroPrefix: [],
    KeySetLen: 0,
    MagnetLen: 0,
  },
  IDStr: '',
  IsEEvision: false,
}

const DefaultDeviceStatus: ResDeviceState = {
  ConnectType: 0,
  IsConnect: false,
  IsSleep: false,
}

export class DeviceManager {
  private static _instance: DeviceManager

  public static get instance() {
    return this._instance || (this._instance = new DeviceManager())
  }

  constructor() {}

  state = reactive({
    currentPid: DevicePid.None,
    deviceList: [] as CurrentDevice[], // exe
    webDeviceList: [] as CurrentDevice[], //hid
    currentDevice: { ...DefaultDevice },
    driverServiceVersion: '0',
    driverProtocol: DeviceProtocol.none,
    deviceStatus: { ...DefaultDeviceStatus },
    isRefreshingDeviceList: false,
    deviceListRefreshDepth: 0,
    isSelectingDevice: false,
    deviceListReadyCount: 0,
  })

  resetState() {
    this.state.currentPid = DevicePid.None
    this.state.currentDevice = { ...DefaultDevice }
    this.state.deviceStatus = { ...DefaultDeviceStatus }
    this.state.driverProtocol = DeviceProtocol.none
  }

  async selectCurrentDevice(pid: DevicePid, mode: DeviceProtocol) {
    this.rememberDeviceSelection(pid, mode)
    this.state.driverProtocol = mode
    this.state.isSelectingDevice = true
    try {
      await DriverService.instance.askToSelect(pid)
      await DriverService.instance.initializeDevice()
    } finally {
      this.state.isSelectingDevice = false
    }
  }

  setExeDeviceList(devices: CurrentDevice[]) {
    this.state.deviceList = devices
    this.markDeviceListReady()
  }

  setWebDeviceList(devices: CurrentDevice[]) {
    this.state.webDeviceList = devices
    this.markDeviceListReady()
  }

  beginDeviceListRefresh() {
    this.state.deviceListRefreshDepth += 1
    this.state.isRefreshingDeviceList = true
  }

  endDeviceListRefresh() {
    this.state.deviceListRefreshDepth = Math.max(0, this.state.deviceListRefreshDepth - 1)
    this.state.isRefreshingDeviceList = this.state.deviceListRefreshDepth > 0
  }

  getRememberedDeviceSelection(): { pid: DevicePid; mode: DeviceProtocol } | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.LAST_SELECTED_DEVICE)
      if (!raw) return null
      const parsed = JSON.parse(raw) as { pid?: unknown; mode?: unknown }
      if (typeof parsed.pid !== 'number' || (parsed.mode !== DeviceProtocol.dll_exe && parsed.mode !== DeviceProtocol.hid_web)) return null
      return { pid: parsed.pid as DevicePid, mode: parsed.mode }
    } catch {
      return null
    }
  }

  setCurrentDevice(id: DevicePid) {
    const deviceList = [...this.state.webDeviceList, ...this.state.deviceList]
    const device = deviceList.filter((e) => e.ID == id)
    if (device.length > 0) {
      this.state.currentDevice = device[0]
      this.state.currentPid = device[0].ID
      if (IS_INTERNAL_BUILD && this.devOverridePids.includes(this.state.currentPid)) {
        this.state.currentPid = DevicePid.CarotmasMars68Pro_Wireless
      }
    }
  }

  private readonly devOverridePids = [DevicePid.MonsGeek, DevicePid.MonsGeek_NoLight, DevicePid.MonsGeek_NoLight_24g]

  private rememberDeviceSelection(pid: DevicePid, mode: DeviceProtocol) {
    if (pid === DevicePid.None || mode === DeviceProtocol.none) return
    try {
      localStorage.setItem(STORAGE_KEYS.LAST_SELECTED_DEVICE, JSON.stringify({ pid, mode }))
    } catch {
      // localStorage can be unavailable in restricted browser modes.
    }
  }

  private markDeviceListReady() {
    this.state.deviceListReadyCount += 1
  }
}
